import { formatServerTimeOnly } from '@foodrun/shared';

/**
 * How an order's state is shown.
 *
 * The database spells these for itself — `dine_in`, `payment_status` — and
 * none of that belongs on a screen someone reads at speed.
 *
 * Void and refunded are deliberately loud. Both mean money did not stay in the
 * business, and both are what someone is looking for when they open this list
 * at all.
 */
export const STATUS_LOOK = {
  new: { label: 'New', tone: 'bg-surface-2 text-ink-2' },
  confirmed: { label: 'Confirmed', tone: 'bg-surface-2 text-ink-2' },
  preparing: { label: 'Cooking', tone: 'bg-warn-bg text-warn' },
  ready: { label: 'Ready', tone: 'bg-good-bg text-good' },
  packed: { label: 'Packed', tone: 'bg-good-bg text-good' },
  dispatched: { label: 'Out', tone: 'bg-good-bg text-good' },
  delivered: { label: 'Delivered', tone: 'bg-good-bg text-good' },
  completed: { label: 'Done', tone: 'bg-good-bg text-good' },
  cancelled: { label: 'Cancelled', tone: 'bg-bad-bg text-bad' },
  refunded: { label: 'Refunded', tone: 'bg-bad-bg text-bad' },
};

/** What to show for a status, including one this build has never seen. */
export function statusLook(status) {
  return STATUS_LOOK[status] ?? { label: status ?? 'Unknown', tone: 'bg-surface-2 text-ink-2' };
}

export function readableType(type) {
  return (
    {
      dine_in: 'Dine in',
      takeaway: 'Takeaway',
      delivery: 'Delivery',
      foodpanda: 'Foodpanda',
      phone_order: 'Phone',
      online: 'Online',
      complimentary: 'Complimentary',
      staff_meal: 'Staff meal',
    }[type] ?? type
  );
}

/**
 * The time of day an order was placed.
 *
 * Only the time: this list is filtered to a single day, so repeating the date
 * on every row is noise. Read as UTC and shown local — see parseServerTime.
 */
export const orderTime = formatServerTimeOnly;

/**
 * A date as the API wants it: YYYY-MM-DD, in local time.
 *
 * Not toISOString().slice(0, 10), which is UTC. Pakistan is five hours ahead,
 * so between midnight and 5am local the UTC date is still yesterday — and a
 * branch trading past midnight would spend those hours looking at the previous
 * day's orders, during a service where someone is usually asking about the
 * order they placed ten minutes ago.
 *
 * Takes a date so it can be tested at a specific moment rather than only at
 * whatever time the tests happen to run.
 */
export function dateKey(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Today, locally. */
export function today() {
  return dateKey(new Date());
}
